import {
  Button,
  Flex,
  Heading,
  IconArrowOpenDownLine,
  IconArrowOpenEndLine,
  IconButton,
  IconEditLine,
  IconTrashLine,
  List,
  Text,
  ToggleButton,
  Tooltip,
} from "@instructure/ui";
import { t } from "i18next";
import React, { useContext, useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { ActionService } from "../../services/ActionService";
import { ExternalService } from "../../services/ExternalService";
import { ReflectionService } from "../../services/ReflectionService";
import { UserDetailContext } from "../../utils/Context";
import { ERROR, useNotification } from "../CustomHooks/NotificationProvider";
import { DialogYesNo } from "../DialogYesNo";
import { ActionEditPanel } from "./ActionEditPanel";

export const Actions = ({ contextId, externalUserName }) => {
  const showNotification = useNotification();
  const userDetails = useContext(UserDetailContext);
  const [keyList, setKeyList] = useState([]);
  const [editedAction, setEditedAction] = useState(null);
  const [actionList, setActionList] = useState([]);
  const [loadReflections, setLoadReflections] = useState({});
  const [loadActions, setLoadActions] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [title, setTitle] = useState(t("actions.addAction"));
  const [actionToDelete, setActionToDelete] = useState(null);

  const actionDraft = {
    action_type: {},
    author: !externalUserName ? userDetails.name : externalUserName,
    reflections: [],
  };

  const handleClose = () => {
    setEditedAction(null);
  };

  useEffect(() => {
    setLoadActions(true);

    const getActions = externalUserName
      ? ExternalService.getExternalActionsForContext
      : ActionService.getActionsForContext;

    getActions(contextId)
      .then((actions) => {
        actions.forEach((obj) => {
          obj.label = obj.text;
        });
        setActionList(actions);
      })
      .catch(() => {
        showNotification(
          t("notifications.general.fetchError", { obj: t("actions.actions") }),
          ERROR
        );
      })
      .finally(() => setLoadActions(false));
  }, [contextId, showNotification]);

  const loadActionReflections = (obj) => {
    if (obj.reflections === null) {
      loadReflections[obj.id] = true;
      setLoadReflections({ ...loadReflections });
      const getReflections = externalUserName
        ? ExternalService.getExternalReflectionsForContext
        : ReflectionService.getReflectionsForContext;

      getReflections(obj.id)
        .then((reflections) => {
          const action = actionList.find(
            (actionObj) => actionObj.id === obj.id
          );
          action.reflections = reflections;
        })
        .catch(() => {
          showNotification(
            t("notifications.general.fetchError", {
              obj: t("reflections.reflections"),
            }),
            ERROR
          );
        })
        .finally(() => {
          delete loadReflections[obj.id];
          setLoadReflections({ ...loadReflections });
        });
    }
  };

  const handleDetails = (key, obj) => {
    if (keyList.includes(key)) {
      const newDetails = keyList.filter((value) => value !== key);
      setKeyList(newDetails);
    } else {
      loadActionReflections(obj);
      setKeyList([...keyList, key]);
    }
  };

  const getContent = (actions) => {
    return loadActions ? (
      <Skeleton
        count={3}
        style={{ height: 64, borderRadius: 5, marginBottom: 10 }}
      />
    ) : !actions || actions.length === 0 ? (
      <Heading level="h5" as="h4">
        {t("actions.noActions")}
      </Heading>
    ) : (
      actions.map((action, key) => (
        <Flex key={key} margin="0 0 medium 0">
          <Flex.Item margin="0 small 0 0" align="start">
            <ToggleButton
              size="small"
              status={keyList.includes(key) ? "pressed" : "unpressed"}
              renderTooltipContent={t("actions.showActions")}
              screenReaderLabel={t("actions.showActions")}
              renderIcon={
                keyList.includes(key)
                  ? IconArrowOpenDownLine
                  : IconArrowOpenEndLine
              }
              onClick={() => handleDetails(key, action)}
            />
          </Flex.Item>
          <Flex.Item shouldShrink shouldGrow>
            <Text as="p" size="small">
              {action.text}
            </Text>
            <Text as="p" fontStyle="italic" size="small">
              {action.author} -{" "}
              {new Date(action.timestamp + "Z").toLocaleString()}
            </Text>
            {keyList.includes(key) && (
              <div style={{ marginTop: 5, marginLeft: 20 }} key={key}>
                <Text as="div" size="small" weight="bold">
                  {t("reflections.reflections")}
                </Text>
                {reflectionsList(action)}
              </div>
            )}
          </Flex.Item>
          <Flex.Item margin="0 small 0 0" align="start">
            <Tooltip
              color="primary-inverse"
              renderTip={t("actions.editAction")}
            >
              <IconButton
                screenReaderLabel={t("actions.editAction")}
                renderIcon={IconEditLine}
                size="small"
                onClick={() => {
                  setTitle(t("actions.editAction"));
                  setEditedAction({ ...action });
                  setOpenModal(true);
                }}
              />
            </Tooltip>
          </Flex.Item>
          <Flex.Item align="start">
            <Tooltip
              color="primary-inverse"
              renderTip={t("actions.deleteAction")}
            >
              <IconButton
                screenReaderLabel={t("actions.deleteAction")}
                renderIcon={IconTrashLine}
                size="small"
                onClick={() => setActionToDelete(action)}
              />
            </Tooltip>
          </Flex.Item>
        </Flex>
      ))
    );
  };

  const reflectionsList = (obj) => {
    if (loadReflections[obj.id]) {
      return <Skeleton count={3} />;
    } else if (obj.reflections && obj.reflections.length > 0) {
      return (
        <List size="small">
          {obj.reflections.map((item, key) => (
            <List.Item key={key}>{item.text}</List.Item>
          ))}
        </List>
      );
    } else {
      return (
        <Heading level="h5" as="h4">
          {t("reflections.noReflections")}
        </Heading>
      );
    }
  };

  const actionUpdated = (updatedAction) => {
    setActionList((prevList) =>
      prevList.map((action) =>
        action.id === updatedAction.id ? updatedAction : action
      )
    );
    setOpenModal(false);
  };

  const actionCreated = (newAction) => {
    setActionList((prevList) => [newAction, ...prevList]);
    setOpenModal(false);
  };

  const handleRemoveAction = async () => {
    const deleteAction = externalUserName
      ? ExternalService.deleteExternalAction
      : ActionService.deleteAction;

    await deleteAction(actionToDelete.id)
      .then(() => {
        setActionList((prevList) =>
          prevList.filter((action) => action.id !== actionToDelete.id)
        );
        setActionToDelete(null);
      })
      .catch(() => {
        showNotification(
          t("notifications.general.deleteError", { obj: t("actions.action") }),
          ERROR
        );
      });
  };

  return (
    <>
      <Flex alignItems="center" margin="xx-small 0 small 0">
        <Flex.Item margin="0 small 0 0">
          <Heading as="h3" level="h4">
            {t("actions.actions")}
          </Heading>
        </Flex.Item>
        <Flex.Item>
          <Button
            size="small"
            onClick={() => {
              setEditedAction({ ...actionDraft });
              setTitle(t("actions.addAction"));
              setOpenModal(true);
            }}
          >
            {t("actions.addAction")}
          </Button>
        </Flex.Item>
      </Flex>

      {getContent(actionList)}

      <ActionEditPanel
        title={title}
        contextId={contextId}
        action={editedAction}
        openModal={openModal}
        setOpenModal={setOpenModal}
        handleActionUpdated={actionUpdated}
        handleActionCreated={actionCreated}
        external={externalUserName ? true : false}
        handleClose={handleClose}
      />
      <DialogYesNo
        message={t("actions.deleteConfirm")}
        openStatus={!!actionToDelete}
        onClickYes={handleRemoveAction}
        onClickNo={() => setActionToDelete(null)}
      />
    </>
  );
};
